import {
  ACTION_FICHE_FOURNISSEUR,
  ACTION_PAYEUR,
  ACTION_TACHE_COLLECTEUR,
  affectationTache,
  ajouterJours,
  ceQuOnAttend,
  commentaireCreation,
  formaterJour,
  formaterPeriode,
  instantDeParis,
  jourAParis,
  preparationDepuisTache,
  promptTacheClaude,
  type ReportTache,
  reportTache,
  type TacheAExpliquer,
  tacheModification,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  corps,
  api,
  ErreurApi,
  type FicheTache,
  ecrit,
  lire,
  requeteMoi,
  requetes,
  type VueTaches,
} from '../api'
import { BoutonClaude } from '../composants/BoutonClaude'
import { filtrerOptions } from '../composants/ChoixCherchable'
import { connexionsDeLaTache, estTacheJustificatifs } from '../composants/bilan-justificatifs'
import { feterTache, vientDEtreFaite } from '../composants/confettis'
import { ATrancherDeLaConnexion } from '../composants/BilanJustificatifs'
import { NavigationFile, useFileTaches } from '../composants/FileParcours'
import { Formulaire } from '../composants/Formulaire'
import { GuideTache } from '../composants/GuideTache'
import { ARefacturer, LiensLot } from '../composants/Refacturations'
import { formaterInstant, Historique } from '../composants/Historique'
import {
  affectationDe,
  BadgesTache,
  type EvenementTache,
  useActionsTache,
} from '../composants/LigneTache'
import { DecisionsAcheminement } from '../composants/Acheminement'
import { DecisionFicheFournisseur } from '../composants/DecisionFicheFournisseur'
import { DecisionPayeur } from '../composants/DecisionPayeur'
import { DepotTache, ZoneDepotTache } from '../composants/DepotTache'
import { DossierMail } from '../composants/DossierMail'
import { ClasserPiece } from '../composants/ClasserPiece'
import { ConsignesTache } from '../composants/ConsignesTache'
import { EnLigne } from '../composants/EnLigne'
import { FilsDriveTache } from '../composants/FilDrive'
import { Icone, type NomIcone } from '../composants/Icone'
import { CollecteurDeTache, RecupererPiece } from '../composants/RecupererPiece'
import { entitesPourPiece, RattacherPiece } from '../composants/RattacherPiece'
import { MailLu } from '../composants/MailLu'
import { PaiementDepuisPiece } from '../composants/PaiementDepuisPiece'
import { PiecesTache } from '../composants/PiecesTache'
import { Section, Vide } from '../composants/Section'
import { ChoixStatutTache } from '../composants/StatutTache'
import { CourrielPropose } from '../composants/CourrielPropose'
import { ProjetDeTache } from '../composants/TachesProjet'
import {
  initiales,
  LIBELLES_STATUT_BROUILLON,
  LIBELLES_STATUT_TACHE,
  LIBELLES_TYPE_BROUILLON,
} from '../libelles'
import { EnvoiDansLaTache } from './BoiteEnvoi'
import { Rapprochement } from './Rapprochement'
import { LogoConnu } from '../composants/Logo'

export function TacheFiche({ id, vue }: { id: string; vue: VueTaches }) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const fiche = useQuery(requetes.tache(id))
  const entites = useQuery(requetes.entites)
  // Tâche d'un collecteur, ou ancienne tâche de justificatifs : la connexion dont le bilan la
  // détaille.
  const portails = useQuery({
    ...requetes.portails,
    enabled: estTacheJustificatifs(fiche.data?.action ?? null),
  })
  const file = useFileTaches(id, vue)
  const { erreur, changer } = useActionsTache()
  // Ouverte par son exécutante : plus nouvelle. La pastille rendue par l'API remplace la
  // précédente, et son changement rafraîchit les listes et cette fiche (PastilleAppli).
  const nouvelle = fiche.data?.nouvelle === true
  useEffect(() => {
    if (!nouvelle) return
    lire(api.taches[':id'].vue.$post({ param: { id } }))
      .then((pastille) => client.setQueryData(requetes.nouvellesTaches.queryKey, pastille))
      .catch(() => undefined)
  }, [client, id, nouvelle])
  const [occupe, setOccupe] = useState(false)
  const [erreurAction, setErreurAction] = useState<string | null>(null)
  const rafraichir = () => client.invalidateQueries()
  const param = { param: { id } }
  // Close ici par une décision, une pièce ou le bouton : confettis (une fois par tâche).
  const statut = fiche.data?.statut
  const statutPrecedent = useRef(statut)
  useEffect(() => {
    if (vientDEtreFaite(statutPrecedent.current, statut)) feterTache(id)
    statutPrecedent.current = statut
  }, [id, statut])
  // Tâche close : on passe à la suivante de la file, s'il y en a une.
  const passerALaSuivante = () => {
    if (file?.suivante) file.aller(file.suivante)
  }

  if (fiche.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!fiche.data) {
    return (
      <main>
        <p className="alerte">Tâche introuvable.</p>
        <Link to="/taches">Retour aux tâches</Link>
      </main>
    )
  }
  const t = fiche.data
  const ecriture =
    moi !== undefined && (t.entiteId === null ? moi.administrateur : ecrit(moi, t.entiteId))
  // Chaque ligne de la fiche se corrige sur place.
  const modifier = async (v: Record<string, unknown>) => {
    await lire(api.taches[':id'].$patch({ ...param, json: corps(tacheModification, v) }))
    await rafraichir()
  }
  const affecter = async (v: Record<string, unknown>) => {
    await lire(api.taches[':id'].affecter.$post({ ...param, json: corps(affectationTache, v) }))
    await rafraichir()
  }
  const agir = async (action: () => Promise<unknown>) => {
    setErreurAction(null)
    try {
      await action()
      await rafraichir()
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }
  const reporter = (r: ReportTache) =>
    agir(() => lire(api.taches[':id'].reporter.$post({ ...param, json: corps(reportTache, r) })))
  // Décisions dont le statut suit leur objet : trancher le rapprochement, traiter la pièce.
  // Acheminement d'une pièce (réglée ou avancée, envoi interrompu, dépôt à la main) : la
  // tâche se ferme quand la pièce est tranchée.
  const acheminement =
    t.pieceId !== null && ACTIONS_ACHEMINEMENT.includes(t.action ?? '') ? t.pieceId : null
  // Facture d'un portail d'achats : qui l'a payée, à qui la refacturer.
  const payeur = t.pieceId !== null && t.action === ACTION_PAYEUR ? t.pieceId : null
  // Pièce rapprochée : compléter ou non la fiche de son fournisseur (décision du 14/10/2026).
  const ficheFournisseur =
    t.pieceId !== null && t.action === ACTION_FICHE_FOURNISSEUR ? t.pieceId : null
  // Propositions de l'assistant sur le plan de classement : tranchées sur la page du plan.
  const planAValider = t.action === 'valider_plan' ? t.entiteId : null
  // Mail et ses pièces : une seule décision, la tâche se ferme quand tout est tranché.
  const dossierMail = t.dossier?.aTrancher ? t.dossier : null
  const decisionDuMail = t.action === 'traiter_mail'
  const decisionPilotee =
    decisionDuMail ||
    acheminement !== null ||
    payeur !== null ||
    ficheFournisseur !== null ||
    planAValider !== null ||
    (t.type === 'decision' &&
      (t.transactionId !== null || (t.action === 'verifier_piece' && t.pieceId !== null)))
  const ouverte = ['a_faire', 'en_cours', 'en_attente_tiers'].includes(t.statut)
  const bilanDeLaTache = connexionsDeLaTache(t, portails.data?.connexions ?? [])
  // Tâche libre (ni échéance, ni paiement, ni pièce) : son entité peut changer.
  const libre = !t.echeanceId && !t.paiementId && !t.pieceId && !t.transactionId
  const entitesEcrites = (entites.data ?? [])
    .filter((e) => !e.archivee && moi && ecrit(moi, e.id))
    .map((e) => ({ valeur: e.id, libelle: e.nom }))
  const executantsProposes = [
    ...t.affectables.executants.map((p) => ({ valeur: `personne:${p.id}`, libelle: p.nom })),
    ...t.affectables.tiers.map((x) => ({ valeur: `tiers:${x.id}`, libelle: `${x.nom} (tiers)` })),
  ]
  // L'exécutant actuel reste dans la liste même s'il n'est plus proposé (assistant, fournisseur) :
  // enregistrer sans y toucher ne le retire pas.
  const executantActuel = t.executantPersonneId
    ? { valeur: `personne:${t.executantPersonneId}`, libelle: t.executant ?? 'Exécutant actuel' }
    : t.executantTiersId
      ? { valeur: `tiers:${t.executantTiersId}`, libelle: `${t.executantTiers ?? '?'} (tiers)` }
      : null
  if (executantActuel && !executantsProposes.some((o) => o.valeur === executantActuel.valeur))
    executantsProposes.push(executantActuel)
  // Une décision à prendre sur une pièce : l'aperçu juste sous la décision.
  const decisionDabord =
    ((decisionPilotee && !decisionDuMail) || dossierMail !== null) && ouverte && ecriture
  // L'entité que suggère le dossier du mail.
  const entiteSuggeree = t.dossier?.entiteProposee ?? null
  const sansEntite = t.entiteId === null && libre && ecriture
  // Une pièce glissée rejoint le mail de la tâche, sinon la tâche libre qui n'en a pas encore
  // (une tâche d'échéance, de paiement ou de pièce reçoit les siennes par la lecture).
  const depotPossible = ecriture && ouverte && (t.mails.length > 0 || libre)
  const mailEtPieces = (t.mails.length > 0 || t.pieces.length > 0 || depotPossible) && (
    <section className="carte carte-mail">
      <h2 className="titre-icone">
        <Icone nom={t.mails.length > 0 ? 'mail' : 'attach_file'} />
        {t.mails.length === 0 ? 'Pièces' : t.pieces.length === 0 ? 'Mail' : 'Mail et pièces'}
        {t.pieces.length > 0 && <span className="mono discret">{t.pieces.length}</span>}
      </h2>
      {t.lotRefacturation && (
        <LiensLot lotId={t.lotRefacturation.id} pieceId={t.lotRefacturation.pieceId} />
      )}
      {t.mails.map((m) => (
        <MailLu key={m.id} m={m} />
      ))}
      {t.pieces.length > 0 ? (
        <PiecesTache t={t} ecriture={ecriture} tacheId={id} />
      ) : (
        <ZoneDepotTache />
      )}
    </section>
  )
  const meta = [
    t.origine === 'mail'
      ? `Reçue le ${formaterInstant(t.creeLe)}`
      : `Créée le ${formaterInstant(t.creeLe)}`,
    t.executant || t.executantTiers ? `confiée à ${affectationDe(t)}` : affectationDe(t),
    t.dateDue ? `pour le ${formaterJour(t.dateDue)}` : 'sans échéance',
  ]

  async function choisir(evenement: EvenementTache, vers: FicheTache['statut']) {
    setOccupe(true)
    const ok = await changer(id, evenement)
    setOccupe(false)
    if (ok && (vers === 'faite' || vers === 'annulee')) passerALaSuivante()
  }

  return (
    <DepotTache tacheId={id} actif={depotPossible}>
      <main className="fiche-tache">
        <div className="barre-fiche">
          <Link
            to="/taches"
            search={{ vue: vue === 'toutes' ? undefined : vue }}
            className="retour"
          >
            <Icone nom="arrow_back" taille={18} />
            Tâches
          </Link>
          <span className="separateur" aria-hidden="true">
            /
          </span>
          <span className="discret">{LIBELLES_VUE[vue]}</span>
          {file && <NavigationFile file={file} objet="tâche" />}
        </div>

        {/* La décision ci-dessous classe déjà la pièce : le fil montre l'emplacement seulement. */}
        <FilsDriveTache
          t={t}
          ecriture={ecriture}
          sansActions={decisionDabord && (dossierMail !== null || t.action === 'choisir_rubrique')}
        />

        <div className="fiche-tache-contenu">
          <header className="entete-tache">
            <div className="puces">
              <span className="badge grand ton-ok">
                <Icone nom={ICONES_ORIGINE[t.origine] ?? 'edit_note'} />
                {LIBELLES_ORIGINE[t.origine] ?? 'Saisie'}
              </span>
              {t.type === 'decision' && <span className="badge grand">Décision</span>}
              {t.entiteId ? (
                <Link
                  to="/entites/$id"
                  params={{ id: t.entiteId }}
                  className="badge grand puce-entite"
                >
                  <Icone nom="domain" />
                  {t.entite}
                </Link>
              ) : libre ? (
                <span className="badge grand ton-ambre">
                  <Icone nom="warning" />
                  Entité non reconnue
                </span>
              ) : null}
              <BadgesTache t={t} />
            </div>
            <EnLigne
              libelle="le titre"
              champs={[{ nom: 'titre', libelle: 'Titre', type: 'texte', requis: true }]}
              initial={t}
              modifiable={ecriture}
              onEnregistrer={modifier}
            >
              <h1 className="titre-logo">
                <LogoConnu nom={t.titre} taille={36} />
                {t.titre}
              </h1>
            </EnLigne>
            <p className="discret meta-tache">{meta.join(' · ')}</p>
          </header>

          <div className="barre-actions-tache">
            <ChoixStatutTache
              statut={t.statut}
              onChoisir={(evenement, vers) => void choisir(evenement, vers)}
              lectureSeule={!ecriture || decisionPilotee}
              occupe={occupe}
            />
            <div className="barre-actions-droite">
              {ecriture && ouverte && (
                <Affectation
                  t={t}
                  onPrendre={() =>
                    agir(() => lire(api.taches[':id'].prendre.$post({ param: { id } })))
                  }
                  onAffecter={(v) => agir(() => affecter(v))}
                />
              )}
              {ecriture && ouverte && <Reporter t={t} onReporter={reporter} />}
              <PlusDActions lienMail={t.mails[0]?.lien ?? null} />
            </div>
          </div>
          {(erreur || erreurAction) && <p className="alerte">{erreur ?? erreurAction}</p>}
          {ouverte && (
            <AFaireIci
              t={{
                id,
                titre: t.titre,
                action: t.action,
                type: t.type,
                entite: t.entite,
                description: t.description,
                typeGuide: t.typeGuide,
                avecMail: t.mails.length > 0,
              }}
            />
          )}
          {ecriture && <CourrielPropose tacheId={id} proposition={t.courrielPropose} />}
          {t.action === 'recuperer_piece' && ouverte && (
            <RecupererPiece tacheId={id} ecriture={ecriture} />
          )}
          {t.action === ACTION_TACHE_COLLECTEUR && ouverte && (
            <CollecteurDeTache tacheId={id} ecriture={ecriture} />
          )}
          {estTacheJustificatifs(t.action) &&
            // La tâche d'un collecteur ne mène au bilan que pour un portail de justificatifs.
            (t.action !== ACTION_TACHE_COLLECTEUR || bilanDeLaTache.length > 0) && (
              <OuvrirLeBilan
                aFournir={t.action === 'justificatifs_a_fournir'}
                aTrancher={
                  ouverte &&
                  (t.action === 'justificatifs_a_trancher' ||
                    (t.action === ACTION_TACHE_COLLECTEUR && / à choisir\b/.test(t.titre)))
                }
                ecriture={ecriture}
                connexions={bilanDeLaTache}
                lu={portails.data !== undefined}
              />
            )}
          <ConsignesTache t={t} ecriture={ecriture} />
          {t.endormie && t.reporteeAu && (
            <div className="bandeau-reponse">
              <strong>
                Reportée
                {t.reporteePeriode && ` au bilan ${formaterPeriode(t.reporteePeriode)}`}
              </strong>{' '}
              : absente des listes jusqu’au {formaterJour(t.reporteeAu)}, elle reviendra alors «
              Nouvelle ».
              {ecriture && (
                <div className="boutons">
                  <button
                    type="button"
                    className="bouton"
                    onClick={() => void reporter({ mode: 'reveiller' })}
                  >
                    La faire revenir maintenant
                  </button>
                </div>
              )}
            </div>
          )}
          {t.misDeCote.length > 0 && (
            <Section titre="Mis de côté pour cette échéance">
              <p className="discret">
                Tâches reportées à cette préparation : à reprendre ici, une par une.
              </p>
              <ul className="liste-mis-de-cote">
                {t.misDeCote.map((m) => (
                  <li key={m.id}>
                    <Link to="/taches/$id" params={{ id: m.id }}>
                      {m.titre}
                    </Link>
                    <span className="badge">{LIBELLES_STATUT_TACHE[m.statut]}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {t.reponseRecueLe && ouverte && t.statut !== 'en_attente_tiers' && (
            <div className="bandeau-reponse">
              <strong>Réponse reçue</strong> de {t.executantTiers ?? 'le tiers attendu'} le{' '}
              {formaterInstant(t.reponseRecueLe)} : la vérifier (mail et pièces ci-dessous), puis
              clore la tâche.
              {ecriture && (
                <div className="boutons">
                  <button
                    type="button"
                    className="bouton bouton-principal"
                    disabled={occupe}
                    onClick={() => void choisir('terminer', 'faite')}
                  >
                    Clore la tâche
                  </button>
                </div>
              )}
            </div>
          )}

          {sansEntite && !(decisionDabord && dossierMail) && (
            <BandeauEntite
              key={entiteSuggeree ?? ''}
              suggeree={entitesEcrites.find((e) => e.valeur === entiteSuggeree) ?? null}
              entites={entitesEcrites}
              onRattacher={(entiteId) => agir(() => modifier({ entiteId }))}
            />
          )}

          {decisionDuMail && ouverte && !dossierMail && (
            <p className="discret">
              Pièces du mail en cours de lecture ou de classement : la tâche se fermera seule quand
              tout sera tranché.
            </p>
          )}

          {decisionDabord && (
            <Section titre="Décision">
              {dossierMail ? (
                <DossierMail
                  tacheId={id}
                  dossier={dossierMail}
                  apres={(r) => {
                    if (r.statut === 'faite') passerALaSuivante()
                  }}
                />
              ) : t.transactionId ? (
                <Rapprochement id={t.transactionId} dansTache apres={passerALaSuivante} />
              ) : planAValider ? (
                <div className="boutons">
                  <Link
                    to="/entites/$id/plan"
                    params={{ id: planAValider }}
                    className="bouton bouton-principal"
                  >
                    Ouvrir le plan de classement
                  </Link>
                </div>
              ) : payeur ? (
                <DecisionPayeur pieceId={payeur} apres={passerALaSuivante} />
              ) : ficheFournisseur ? (
                <DecisionFicheFournisseur pieceId={ficheFournisseur} apres={passerALaSuivante} />
              ) : acheminement ? (
                <DecisionAcheminement
                  pieceId={acheminement}
                  classer={t.action === 'choisir_rubrique'}
                />
              ) : (
                t.pieceId && <DecisionPiece pieceId={t.pieceId} apres={passerALaSuivante} />
              )}
            </Section>
          )}

          {mailEtPieces}

          {t.pieceId && <PaiementDepuisPiece pieceId={t.pieceId} />}

          <section className="carte carte-details">
            <h2>Détails</h2>
            <dl className="details-grille">
              {libre && (
                <div>
                  <dt>Entité</dt>
                  <dd>
                    <EnLigne
                      libelle="l’entité"
                      champs={[
                        {
                          nom: 'entiteId',
                          libelle: 'Entité',
                          type: 'choix',
                          options: entitesEcrites,
                          aide: 'Mail classé sur la mauvaise entité : la tâche et ses mails y passent.',
                        },
                      ]}
                      initial={t}
                      modifiable={ecriture}
                      onEnregistrer={modifier}
                    >
                      {t.entite ?? 'Aucune'}
                    </EnLigne>
                  </dd>
                </div>
              )}
              <div>
                <dt>Pour le</dt>
                <dd>
                  <EnLigne
                    libelle="la date"
                    champs={[{ nom: 'dateDue', libelle: 'Pour le', type: 'date' }]}
                    initial={t}
                    modifiable={ecriture}
                    // La date n'est « forcée » que si elle change.
                    onEnregistrer={(v) =>
                      v.dateDue === t.dateDue ? Promise.resolve() : modifier(v)
                    }
                  >
                    {t.dateDue ? formaterJour(t.dateDue) : 'Sans date'}
                    {t.dateForcee && ' (saisie à la main)'}
                  </EnLigne>
                  {t.dateForcee && ecriture && t.origine === 'generee' && (
                    <button
                      type="button"
                      className="bouton-texte"
                      onClick={async () => {
                        await lire(
                          api.taches[':id'].$patch({ ...param, json: { dateForcee: false } }),
                        )
                        await rafraichir()
                      }}
                    >
                      Rendre la date au générateur
                    </button>
                  )}
                </dd>
              </div>
              <div>
                <dt>Exécutant</dt>
                <dd>
                  <EnLigne
                    libelle="l’exécutant"
                    champs={[
                      {
                        nom: 'executant',
                        libelle: 'Exécutant',
                        type: 'choix',
                        options: executantsProposes,
                        aide: 'Quelqu’un qui a un rôle sur l’entité, le cabinet ou le gestionnaire. Vide : à prendre par les garants.',
                      },
                    ]}
                    initial={{
                      executant: t.executantPersonneId
                        ? `personne:${t.executantPersonneId}`
                        : t.executantTiersId
                          ? `tiers:${t.executantTiersId}`
                          : null,
                    }}
                    modifiable={ecriture}
                    onEnregistrer={(v) => {
                      const [genre, cible] = String(v.executant ?? ':').split(':')
                      return affecter({
                        executantPersonneId: genre === 'personne' ? cible : null,
                        executantTiersId: genre === 'tiers' ? cible : null,
                      })
                    }}
                  >
                    {affectationDe(t)}
                  </EnLigne>
                </dd>
              </div>
              <div>
                <dt>Garant</dt>
                <dd>
                  <EnLigne
                    libelle="le garant"
                    champs={[
                      {
                        nom: 'garantId',
                        libelle: 'Garant',
                        type: 'choix',
                        options: t.affectables.garants.map((p) => ({
                          valeur: p.id,
                          libelle: p.nom,
                        })),
                        aide: 'Vide : les responsables de l’entité.',
                      },
                    ]}
                    initial={t}
                    modifiable={ecriture}
                    onEnregistrer={affecter}
                  >
                    {t.garant ?? `${t.garants.map((g) => g.nom).join(', ')} (responsables)`}
                  </EnLigne>
                </dd>
              </div>
              <div>
                <dt>Urgente</dt>
                <dd>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={t.urgente}
                    className="interrupteur"
                    disabled={!ecriture}
                    onClick={() => void agir(() => modifier({ urgente: !t.urgente }))}
                  >
                    <span className="interrupteur-piste" />
                    {t.urgente ? 'Oui' : 'Non'}
                  </button>
                </dd>
              </div>
              <div>
                <dt>Origine</dt>
                <dd>
                  {LIBELLES_ORIGINE[t.origine] ?? 'Saisie'}
                  {t.cycle > 0 && ', rouverte'}
                </dd>
              </div>
              {t.echeanceId && (
                <div>
                  <dt>Échéance</dt>
                  <dd>
                    <Link to="/echeances/$id" params={{ id: t.echeanceId }}>
                      {t.echeance} {t.periode}
                    </Link>
                  </dd>
                </div>
              )}
              {t.paiement && (
                <div>
                  <dt>Paiement</dt>
                  <dd>{t.paiement}</dd>
                </div>
              )}
              {(t.projetId || (ecriture && !t.tacheParenteId)) && (
                <div>
                  <dt>Projet</dt>
                  <dd>
                    <ProjetDeTache t={t} ecriture={ecriture} />
                  </dd>
                </div>
              )}
              {(t.description || ecriture) && (
                <div className="large">
                  <dt>Précisions</dt>
                  <dd>
                    <EnLigne
                      libelle="les précisions"
                      champs={[
                        { nom: 'description', libelle: 'Précisions', type: 'zone', lignes: 4 },
                      ]}
                      initial={t}
                      modifiable={ecriture}
                      onEnregistrer={modifier}
                    >
                      {t.description ? (
                        <Precisions texte={t.description} />
                      ) : (
                        <span className="discret">Aucune</span>
                      )}
                    </EnLigne>
                  </dd>
                </div>
              )}
            </dl>
          </section>

          {/* Un guide se propose pour une tâche saisie, jamais pour une tâche générée qui a son action. */}
          <GuideTache tacheId={id} ecriture={ecriture && ouverte} libre={libre && !t.action} />

          {t.action === 'refacturer' && t.entiteId && (
            <section className="carte section">
              <div className="section-titre">
                <h2>Frais à refacturer</h2>
              </div>
              <ARefacturer entiteId={t.entiteId} />
            </section>
          )}

          <Preparer tacheId={id} dateDue={t.dateDue} ecriture={ecriture && ouverte} />

          <CommentairesEtHistorique
            id={id}
            commentaires={t.commentaires}
            ecriture={ecriture}
            onCommenter={async (v) => {
              await lire(
                api.taches[':id'].commentaires.$post({
                  ...param,
                  json: corps(commentaireCreation, v),
                }),
              )
              await rafraichir()
            }}
          />
        </div>
      </main>
    </DepotTache>
  )
}

/** Marque d'une pièce dans un texte de tâche (rattrapage) : `[pièce <id>]`, `[pièce <id>, à lire]`. */
const MARQUE_PIECE = /\[pièce ([0-9a-f-]{36})(, à lire)?\]/g

/** Les précisions d'une tâche, lignes gardées ; une pièce marquée devient un lien vers elle. */
function Precisions({ texte }: { texte: string }) {
  const morceaux: ReactNode[] = []
  let depuis = 0
  for (const m of texte.matchAll(MARQUE_PIECE)) {
    morceaux.push(texte.slice(depuis, m.index))
    morceaux.push(
      <Link key={m.index} to="/pieces/$id" params={{ id: m[1]! }}>
        {m[2] ? 'voir la pièce (en lecture)' : 'voir la pièce'}
      </Link>,
    )
    depuis = m.index + m[0].length
  }
  morceaux.push(texte.slice(depuis))
  return <span className="precisions">{morceaux}</span>
}

/** Réglages où se règle une tâche de connexion (portail, boîte mail, banque). */
const REGLAGES_DE: Record<
  string,
  { connexion: 'portails' | 'boites' | 'banques'; libelle: string }
> = {
  verifier_portail: { connexion: 'portails', libelle: 'Ouvrir les portails' },
  [ACTION_TACHE_COLLECTEUR]: { connexion: 'portails', libelle: 'Ouvrir les portails' },
  reconnecter_boite: { connexion: 'boites', libelle: 'Ouvrir les boîtes mail' },
  renouveler_banque: { connexion: 'banques', libelle: 'Ouvrir les banques' },
}

/**
 * « À faire ici » (décision du 13/10/2026, audit des tâches) : ce que la tâche attend, dit d'abord,
 * en une phrase et quelques étapes, avec « Traiter avec Claude » (sauf pour récupérer une pièce,
 * qui a le sien) et, pour une tâche de connexion, le lien vers ses réglages.
 */
function AFaireIci({ t }: { t: TacheAExpliquer }) {
  const c = ceQuOnAttend(t)
  const reglages = t.action ? REGLAGES_DE[t.action] : undefined
  const prompt = promptTacheClaude(t, `${window.location.origin}/taches/${t.id}`)
  return (
    <section className="carte a-faire-ici">
      <h2 className="titre-icone">
        <Icone nom="checklist" />À faire ici
      </h2>
      <p className="a-faire-resume">{c.resume}</p>
      {c.etapes.length > 0 && (
        <ol className="a-faire-etapes">
          {c.etapes.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ol>
      )}
      <div className="boutons">
        {reglages && (
          <Link
            to="/reglages"
            search={{ onglet: 'connexions', connexion: reglages.connexion }}
            className="bouton bouton-principal"
          >
            <Icone nom="settings" />
            {reglages.libelle}
          </Link>
        )}
        {t.action !== 'recuperer_piece' && <BoutonClaude prompt={prompt} aide={false} />}
      </div>
    </section>
  )
}

/**
 * Tâche « justificatifs à choisir » ou « à fournir » : elle se décide dans le bilan de sa
 * connexion ; « à choisir » montre ici même ses opérations et leurs pièces candidates
 * (« C'est celle-ci », relié par l'agent au prochain passage). La tâche se recompte et se ferme
 * seule.
 */
function OuvrirLeBilan({
  aFournir,
  aTrancher,
  ecriture,
  connexions,
  lu,
}: {
  aFournir: boolean
  aTrancher: boolean
  ecriture: boolean
  connexions: { id: string; libelle: string }[]
  lu: boolean
}) {
  return (
    <section className="carte section">
      <div className="section-titre">
        <h2>
          <Icone nom="receipt_long" />
          Bilan des justificatifs
        </h2>
      </div>
      {/* « À choisir » : l'encart « À faire ici » dit déjà le geste, la liste suit. */}
      {!aTrancher && (
        <p className="discret">
          {aFournir
            ? 'Opérations sans pièce dans l’appli : « Chercher dans les mails » dans le bilan, déposer la facture dans l’appli (elle compte au calcul suivant), ou dire qu’aucun justificatif n’est attendu.'
            : 'Choisir la pièce de chaque opération (« C’est celle-ci ») : l’agent la relie chez le cabinet au prochain passage. La relier soi-même chez le cabinet marche aussi : le passage suivant la reconnaît.'}{' '}
          La tâche se recompte à chaque calcul et se ferme seule quand il ne reste rien
          {aFournir && ' ; fermée à la main, elle ne revient que le mois suivant'}.
        </p>
      )}
      {aTrancher && connexions.length > 0 ? (
        connexions.map((c) => (
          <div key={c.id}>
            {connexions.length > 1 && <h3>{c.libelle}</h3>}
            <ATrancherDeLaConnexion connexionId={c.id} ecriture={ecriture} aide={false} />
          </div>
        ))
      ) : connexions.length > 0 ? (
        <div className="boutons">
          {connexions.map((c) => (
            <Link
              key={c.id}
              to="/justificatifs/$id"
              params={{ id: c.id }}
              className="bouton bouton-principal"
            >
              <Icone nom="receipt_long" />
              Ouvrir le bilan{connexions.length > 1 ? ` (${c.libelle})` : ''}
            </Link>
          ))}
        </div>
      ) : (
        lu && <p className="discret">Connexion introuvable : elle a pu être supprimée.</p>
      )}
    </section>
  )
}

const LIBELLES_VUE: Record<VueTaches, string> = {
  ma_semaine: 'Ma semaine',
  tableau: 'Tableau',
  en_attente_tiers: 'En attente de tiers',
  reportees: 'Reportées',
  toutes: 'Toutes',
}

const LIBELLES_ORIGINE: Record<string, string> = {
  generee: 'Générée par une règle',
  mail: 'Reçue par mail',
  manuelle: 'Saisie',
}
const ICONES_ORIGINE: Record<string, NomIcone> = {
  generee: 'event_repeat',
  mail: 'mail',
  manuelle: 'edit_note',
}

/** À partir de ce nombre de choix, le menu « Assigner à » a sa recherche. */
const RECHERCHE_DES = 8

/**
 * « Je prends » et, à côté, « Assigner à… » : une personne de l'appli, le cabinet ou le
 * gestionnaire (`proposeCommeExecutant`). Liste longue : on tape quelques lettres du nom (Entrée
 * choisit le premier, Échap referme).
 */
function Affectation({
  t,
  onPrendre,
  onAffecter,
}: {
  t: FicheTache
  onPrendre: () => Promise<void>
  onAffecter: (v: Record<string, unknown>) => Promise<void>
}) {
  const [ouvert, setOuvert] = useState(false)
  const [recherche, setRecherche] = useState('')
  const moi = useQuery(requeteMoi).data
  // Une fois confiée, le bouton dit à qui : on voit d'un coup d'œil que c'est fait.
  const confieeA = t.executant ?? t.executantTiers
  const choix = [
    ...t.affectables.executants.map((p) => ({
      cle: `p:${p.id}`,
      nom: p.nom,
      role: p.id === moi?.id ? 'vous' : 'personne de l’appli',
      actuel: p.id === t.executantPersonneId,
      v: { executantPersonneId: p.id },
    })),
    ...t.affectables.tiers.map((x) => ({
      cle: `t:${x.id}`,
      nom: x.nom,
      role: `tiers · ${x.role}`,
      actuel: x.id === t.executantTiersId,
      v: { executantTiersId: x.id },
    })),
  ]
  const trouves = filtrerOptions(
    choix.map((c) => ({ ...c, valeur: c.cle, libelle: c.nom })),
    recherche,
  )
  const basculer = () => {
    setOuvert(!ouvert)
    setRecherche('')
  }
  const choisir = (c: (typeof choix)[number]) => {
    setOuvert(false)
    if (!c.actuel) void onAffecter(c.v)
  }
  return (
    <div className="menu-ancre">
      <div className="bouton-scinde">
        {t.aPrendre ? (
          <button type="button" onClick={() => void onPrendre()}>
            <Icone nom="front_hand" taille={18} />
            Je prends
          </button>
        ) : confieeA ? (
          <button type="button" title={`Confiée à ${confieeA} : changer`} onClick={basculer}>
            <span className="avatar petit">{initiales(confieeA).slice(0, 2)}</span>
            {confieeA}
          </button>
        ) : (
          <button type="button" onClick={basculer}>
            <Icone nom="person_add" taille={18} />
            Assigner
          </button>
        )}
        <button
          type="button"
          className="fleche"
          title="Assigner à…"
          aria-label="Assigner à…"
          aria-expanded={ouvert}
          onClick={basculer}
        >
          <Icone nom="keyboard_arrow_down" />
        </button>
      </div>
      {ouvert && (
        <div className="menu-deroulant" role="menu">
          <span className="surtitre">Assigner à</span>
          {choix.length >= RECHERCHE_DES && (
            <div className="menu-recherche">
              <input
                type="search"
                aria-label="Chercher une personne ou un tiers"
                placeholder="Chercher…"
                autoComplete="off"
                autoFocus
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const premier = trouves[0]
                    if (premier) choisir(premier)
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setOuvert(false)
                  }
                }}
              />
            </div>
          )}
          {trouves.length === 0 && <span className="menu-vide discret">Aucun résultat.</span>}
          {trouves.map((c) => (
            <button
              key={c.cle}
              type="button"
              role="menuitemradio"
              aria-checked={c.actuel}
              onClick={() => choisir(c)}
            >
              <span className="avatar petit">{initiales(c.nom).slice(0, 2)}</span>
              <span className="menu-libelle">
                <span>{c.nom}</span>
                <span className="discret">{c.role}</span>
              </span>
              {c.actuel && <Icone nom="check" taille={18} />}
            </button>
          ))}
          {(t.executantPersonneId || t.executantTiersId) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOuvert(false)
                void onAffecter({ executantPersonneId: null, executantTiersId: null })
              }}
            >
              <span className="avatar petit">?</span>
              <span className="menu-libelle">
                <span>Personne</span>
                <span className="discret">à prendre par les garants</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Reporter : la tâche sort des listes jusqu'au jour choisi (ou la préparation du bilan de son
 * entité), puis revient « Nouvelle ». Le motif, facultatif, devient un commentaire.
 */
function Reporter({
  t,
  onReporter,
}: {
  t: FicheTache
  onReporter: (r: ReportTache) => Promise<void>
}) {
  const [ouvert, setOuvert] = useState(false)
  const [jour, setJour] = useState('')
  const [motif, setMotif] = useState('')
  const aujourdhui = jourAParis(new Date())
  const choisir = (r: { mode: 'bilan' } | { mode: 'date'; au: string }) => {
    setOuvert(false)
    void onReporter({ ...r, ...(motif.trim() && { motif: motif.trim() }) })
  }
  return (
    <div className="menu-ancre">
      <button
        type="button"
        className="bouton-icone cadre"
        title="Reporter"
        aria-label="Reporter"
        aria-expanded={ouvert}
        onClick={() => setOuvert(!ouvert)}
      >
        <Icone nom="snooze" />
      </button>
      {ouvert && (
        <div className="menu-deroulant menu-report" role="menu">
          <span className="surtitre">Reporter : absente des listes jusque-là</span>
          <div className="menu-recherche">
            <input
              type="text"
              aria-label="Motif du report"
              placeholder="Motif (facultatif)"
              maxLength={5000}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </div>
          {t.entiteId && (
            <button type="button" role="menuitem" onClick={() => choisir({ mode: 'bilan' })}>
              <Icone nom="account_balance" taille={18} />
              <span className="menu-libelle">
                <span>À la préparation du bilan</span>
                <span className="discret">{t.entite} : revient avec sa première tâche</span>
              </span>
            </button>
          )}
          {[
            { libelle: 'Dans une semaine', jours: 7 },
            { libelle: 'Dans un mois', jours: 30 },
          ].map((c) => (
            <button
              key={c.jours}
              type="button"
              role="menuitem"
              onClick={() => choisir({ mode: 'date', au: ajouterJours(aujourdhui, c.jours) })}
            >
              <Icone nom="event_upcoming" taille={18} />
              <span className="menu-libelle">
                <span>{c.libelle}</span>
                <span className="discret">
                  le {formaterJour(ajouterJours(aujourdhui, c.jours))}
                </span>
              </span>
            </button>
          ))}
          <form
            className="menu-report-jour"
            onSubmit={(e) => {
              e.preventDefault()
              if (jour > aujourdhui) choisir({ mode: 'date', au: jour })
            }}
          >
            <input
              type="date"
              aria-label="Jour du retour"
              min={ajouterJours(aujourdhui, 1)}
              value={jour}
              onChange={(e) => setJour(e.target.value)}
            />
            <button type="submit" className="bouton" disabled={!(jour > aujourdhui)}>
              Reporter
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

/** Plus d'actions : ouvrir le mail d'origine, copier le lien de la tâche. */
function PlusDActions({ lienMail }: { lienMail: string | null }) {
  const [ouvert, setOuvert] = useState(false)
  const [copie, setCopie] = useState(false)
  return (
    <div className="menu-ancre">
      <button
        type="button"
        className="bouton-icone cadre"
        title="Plus d’actions"
        aria-label="Plus d’actions"
        aria-expanded={ouvert}
        onClick={() => setOuvert(!ouvert)}
      >
        <Icone nom="more_horiz" />
      </button>
      {ouvert && (
        <div className="menu-deroulant" role="menu">
          {lienMail && (
            <a
              href={lienMail}
              target="_blank"
              rel="noreferrer"
              role="menuitem"
              onClick={() => setOuvert(false)}
            >
              <Icone nom="open_in_new" taille={18} />
              Ouvrir le mail
            </a>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href)
                setCopie(true)
              } finally {
                setOuvert(false)
              }
            }}
          >
            <Icone nom="link" taille={18} />
            {copie ? 'Lien copié' : 'Copier le lien de la tâche'}
          </button>
        </div>
      )}
    </div>
  )
}

/** Tâche sans entité : la suggestion (dossier du mail), un choix, rattacher. */
function BandeauEntite({
  suggeree,
  entites,
  onRattacher,
}: {
  suggeree: { valeur: string; libelle: string } | null
  entites: { valeur: string; libelle: string }[]
  onRattacher: (entiteId: string) => Promise<void>
}) {
  const [choisie, setChoisie] = useState(suggeree?.valeur ?? '')
  return (
    <div className="bandeau-entite">
      <span className="tuile-icone ambre">
        <Icone nom="domain_disabled" />
      </span>
      <div className="bandeau-entite-texte">
        <strong>Aucune entité rattachée</strong>
        <span>
          {suggeree ? (
            <>
              Suggestion de l’assistant : <strong>{suggeree.libelle}</strong>.
            </>
          ) : (
            'Choisir l’entité concernée : la tâche et son mail y passent.'
          )}
        </span>
      </div>
      <select aria-label="Entité" value={choisie} onChange={(e) => setChoisie(e.target.value)}>
        <option value="">Choisir…</option>
        {entites.map((e) => (
          <option key={e.valeur} value={e.valeur}>
            {e.libelle}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="bouton"
        disabled={!choisie}
        onClick={() => void onRattacher(choisie)}
      >
        <Icone nom="link" taille={18} />
        Rattacher
      </button>
    </div>
  )
}

/** Commentaires et historique, en onglets. */
function CommentairesEtHistorique({
  id,
  commentaires,
  ecriture,
  onCommenter,
}: {
  id: string
  commentaires: FicheTache['commentaires']
  ecriture: boolean
  onCommenter: (v: Record<string, unknown>) => Promise<void>
}) {
  const [onglet, setOnglet] = useState<'commentaires' | 'historique'>('commentaires')
  return (
    <section className="carte">
      <div className="onglets-soulignes" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={onglet === 'commentaires'}
          onClick={() => setOnglet('commentaires')}
        >
          Commentaires <span className="mono">{commentaires.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={onglet === 'historique'}
          onClick={() => setOnglet('historique')}
        >
          Historique
        </button>
      </div>
      {onglet === 'commentaires' ? (
        <>
          {commentaires.length === 0 && <Vide>Aucun commentaire.</Vide>}
          {commentaires.map((c) => (
            <div key={c.id} className="ligne">
              <div className="ligne-contenu">
                {c.texte}
                <div className="discret">
                  {c.auteur} · {formaterInstant(c.creeLe)}
                </div>
              </div>
            </div>
          ))}
          {ecriture && (
            <Formulaire
              key={commentaires.length}
              champs={[
                { nom: 'texte', libelle: 'Nouveau commentaire', type: 'texte', requis: true },
              ]}
              libelleBouton="Commenter"
              onValider={onCommenter}
            />
          )}
        </>
      ) : (
        <Historique objetId={id} />
      )}
    </section>
  )
}

const ACTIONS_ACHEMINEMENT = [
  'decider_reglement',
  'verifier_envoi_cabinet',
  'deposer_au_cabinet',
  'choisir_rubrique',
]

/** Décision d'acheminement : les mêmes boutons que sur la fiche de la pièce. */
function DecisionAcheminement({ pieceId, classer }: { pieceId: string; classer: boolean }) {
  const piece = useQuery(requetes.piece(pieceId))
  const p = piece.data
  if (!p) return <Vide>{piece.isPending ? 'Chargement…' : 'Pièce introuvable.'}</Vide>
  return (
    <>
      {/* Rubrique de classement à choisir : le même geste que sur la fiche de la pièce. */}
      {classer && <ClasserPiece p={p} ecriture />}
      <DecisionsAcheminement p={p} />
      <div className="boutons">
        <a
          href={`/api/pieces/${pieceId}/fichier`}
          target="_blank"
          rel="noreferrer"
          className="bouton-discret"
          title="Le document tel qu’il a été reçu"
        >
          Voir le PDF
        </a>
        <Link
          to="/pieces/$id"
          params={{ id: pieceId }}
          className="bouton-discret"
          title="La pièce dans l’appli : lecture, montants, paiement, classement"
        >
          Fiche de la pièce
        </Link>
      </div>
    </>
  )
}

/**
 * « Vérifier la pièce » : la rattacher à une entité ou la rejeter (publicité, sans rapport,
 * doublon). L'un comme l'autre ferme la décision, comme depuis la fiche de la pièce.
 */
function DecisionPiece({ pieceId, apres }: { pieceId: string; apres: () => void }) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const piece = useQuery(requetes.piece(pieceId))
  const entites = useQuery(requetes.entites)
  const [erreur, setErreur] = useState<string | null>(null)
  const p = piece.data
  if (!p) return <Vide>{piece.isPending ? 'Chargement…' : 'Pièce introuvable.'}</Vide>
  const ecrivables = entitesPourPiece(p, entites.data ?? [], moi)

  async function rejeter() {
    setErreur(null)
    try {
      await lire(
        api.pieces[':id'].action.$post({ param: { id: pieceId }, json: { action: 'rejeter' } }),
      )
      await client.invalidateQueries()
      apres()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }

  // Ce qui aide à décider : d'où vient la pièce et pourquoi l'appli n'a pas su.
  const origine = [
    p.expediteur ? `reçue de ${p.expediteur}` : null,
    p.objetMail ? `objet du mail « ${p.objetMail} »` : null,
    p.indication ? `indication « ${p.indication} »` : null,
  ].filter(Boolean)
  return (
    <>
      {origine.length > 0 && <p>Pièce {origine.join(', ')}.</p>}
      {p.motifs.length > 0 && (
        <p className="discret">L’appli n’a pas su trancher : {p.motifs.join(' ')}</p>
      )}
      <p className="discret">
        Rattacher la pièce la range dans son entité ; la rejeter l’écarte (publicité, pièce sans
        rapport, doublon). Les deux ferment cette décision.
      </p>
      {ecrivables.length > 0 && (
        <RattacherPiece
          p={p}
          ecrivables={ecrivables}
          libelleBouton="Rattacher la pièce"
          onFait={apres}
        />
      )}
      <div className="boutons">
        {p.statut !== 'ignoree' && (
          <button
            type="button"
            className="bouton-discret bouton-danger"
            onClick={() => void rejeter()}
          >
            <Icone nom="delete" /> Rejeter la pièce
          </button>
        )}
        <Link
          to="/pieces/$id"
          params={{ id: pieceId }}
          className="bouton-discret"
          title="La pièce dans l’appli : lecture, montants, paiement, classement"
        >
          Fiche de la pièce
        </Link>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </>
  )
}

/**
 * Préparer depuis la tâche (N1) : un courriel ou un courrier au tiers qui l'exécute, la
 * consigne d'un agent navigateur (Claude desktop), ou un rendez-vous. Rien ne part : on
 * relit, on valide et on agit soi-même depuis la boîte d'envoi ; un rendez-vous validé est
 * posé dans l'agenda choisi.
 */
/**
 * Les envois de la tâche (toujours visibles, même fermée ou en lecture : la tâche mène à ses
 * envois comme l'envoi à sa tâche) et, si l'on peut écrire, en préparer d'autres.
 */
function Preparer({
  tacheId,
  dateDue,
  ecriture,
}: {
  tacheId: string
  dateDue: string | null
  ecriture: boolean
}) {
  const client = useQueryClient()
  const envois = useQuery(requetes.brouillons({ tacheId, vue: 'tous' }))
  // Agent navigateur : fermé, en lecture (suggestion de Claude), ou ouvert avec ses valeurs.
  const [navigation, setNavigation] = useState<
    | { etape: 'ferme' }
    | { etape: 'lecture' }
    | { etape: 'ouvert'; objectif: string | null; site: string | null; note: string | null }
  >({ etape: 'ferme' })
  // Rendez-vous : fermé, en lecture (suggestion de Claude), ou ouvert avec ses valeurs.
  const [rendezVous, setRendezVous] = useState<
    | { etape: 'ferme' }
    | { etape: 'lecture' }
    | {
        etape: 'ouvert'
        initial: Record<string, string | null>
        note: string | null
      }
  >({ etape: 'ferme' })
  const [erreur, setErreur] = useState<string | null>(null)
  // Courriel ou courrier en cours de rédaction par Claude (quelques secondes).
  const [redaction, setRedaction] = useState<'courriel' | 'courrier' | null>(null)
  const ouvrirNavigation = async () => {
    setErreur(null)
    setNavigation({ etape: 'lecture' })
    try {
      const r = await lire(api.taches[':id'].suggerer.$post({ param: { id: tacheId } }))
      setNavigation({
        etape: 'ouvert',
        objectif: r.objectif,
        site: r.site,
        note:
          r.mailsIlisibles > 0
            ? 'Mail d’origine illisible (boîte déconnectée ?) : champs proposés d’après la tâche seule, à relire.'
            : 'Champs proposés d’après la tâche et son mail : à relire.',
      })
    } catch (e) {
      setNavigation({
        etape: 'ouvert',
        objectif: null,
        site: null,
        note: e instanceof ErreurApi ? e.message : 'Suggestion impossible : remplir à la main.',
      })
    }
  }
  const ouvrirRendezVous = async () => {
    setErreur(null)
    setRendezVous({ etape: 'lecture' })
    try {
      const r = await lire(
        api.taches[':id']['suggerer-rendez-vous'].$post({ param: { id: tacheId } }),
      )
      const manque = [
        !r.jourLu && 'le jour (échéance de la tâche par défaut)',
        !r.heureDebut && 'l’heure',
        !r.lieu && 'le lieu',
      ].filter(Boolean)
      setRendezVous({
        etape: 'ouvert',
        initial: {
          objectif: r.titre,
          jour: r.jour,
          heureDebut: r.heureDebut,
          heureFin: r.heureFin,
          lieu: r.lieu,
        },
        note: [
          r.mailsIlisibles > 0
            ? 'Mail d’origine illisible (boîte déconnectée ?) : champs proposés d’après la tâche seule, à relire.'
            : 'Champs proposés d’après la tâche et son mail : à relire.',
          manque.length > 0 ? `Non trouvé dans le mail : ${manque.join(', ')}.` : null,
        ]
          .filter(Boolean)
          .join(' '),
      })
    } catch (e) {
      setRendezVous({
        etape: 'ouvert',
        initial: { jour: dateDue },
        note: e instanceof ErreurApi ? e.message : 'Suggestion impossible : remplir à la main.',
      })
    }
  }
  // L'envoi préparé s'ouvre ici même : il se relit et se valide dans la tâche.
  const [relu, setRelu] = useState<string | null>(null)
  // Replié tant qu'on n'en a pas besoin ; ouvert d'emblée s'il porte déjà des envois.
  const [deplie, setDeplie] = useState(false)
  const preparer = async (v: Record<string, unknown>) => {
    const b = await lire(
      api.taches[':id'].preparer.$post({
        param: { id: tacheId },
        json: corps(preparationDepuisTache, v),
      }),
    )
    await client.invalidateQueries({ queryKey: ['brouillons'] })
    setNavigation({ etape: 'ferme' })
    setRendezVous({ etape: 'ferme' })
    setRelu(b.id)
  }
  if (!ecriture && (envois.data?.length ?? 0) === 0) return null
  if (ecriture && !deplie && (envois.data?.length ?? 0) === 0)
    return (
      <section className="carte section">
        <button
          type="button"
          className="bouton-discret bouton-preparer"
          onClick={() => setDeplie(true)}
        >
          <Icone nom="edit_note" />
          Préparer un courriel, un courrier, une consigne d’agent ou un rendez-vous
        </button>
      </section>
    )
  return (
    <Section titre={ecriture ? 'Préparer' : 'Envois'}>
      {ecriture && (
        <>
          <p className="discret">
            Un courriel ou un courrier au tiers qui exécute, une consigne pour l’agent navigateur de
            Claude ou un rendez-vous pour l’agenda (jour, heure et lieu lus dans la convocation),
            préparés d’après la tâche et son mail. Rien ne part : on relit et on valide ici, dans la
            tâche, puis on agit soi-même (un rendez-vous validé est posé dans l’agenda choisi). Ils
            restent visibles dans la boîte d’envoi.
          </p>
          <div className="boutons">
            {(['courriel', 'courrier'] as const).map((type) => (
              <button
                key={type}
                type="button"
                className="bouton"
                disabled={redaction !== null}
                onClick={async () => {
                  try {
                    setErreur(null)
                    setRedaction(type)
                    await preparer({ type })
                  } catch (e) {
                    setErreur(e instanceof ErreurApi ? e.message : 'Préparation impossible.')
                  } finally {
                    setRedaction(null)
                  }
                }}
              >
                {redaction === type ? 'Rédaction…' : LIBELLES_TYPE_BROUILLON[type]}
              </button>
            ))}
            {navigation.etape === 'ferme' && (
              <button type="button" className="bouton" onClick={() => void ouvrirNavigation()}>
                {LIBELLES_TYPE_BROUILLON.navigation}
              </button>
            )}
            {rendezVous.etape === 'ferme' && (
              <button type="button" className="bouton" onClick={() => void ouvrirRendezVous()}>
                {LIBELLES_TYPE_BROUILLON.rendez_vous}
              </button>
            )}
          </div>
          {rendezVous.etape === 'lecture' && (
            <p className="discret">Lecture de la tâche et de son mail…</p>
          )}
          {rendezVous.etape === 'ouvert' && rendezVous.note && (
            <p className="discret">{rendezVous.note}</p>
          )}
          {rendezVous.etape === 'ouvert' && (
            <Formulaire
              champs={[
                {
                  nom: 'objectif',
                  libelle: 'Titre',
                  type: 'texte',
                  aide: 'Vide : le titre de la tâche.',
                },
                { nom: 'jour', libelle: 'Jour', type: 'date', requis: true },
                {
                  nom: 'heureDebut',
                  libelle: 'Début (heure de Paris)',
                  type: 'heure',
                  requis: true,
                },
                { nom: 'heureFin', libelle: 'Fin', type: 'heure', aide: 'Vide : une heure.' },
                { nom: 'lieu', libelle: 'Lieu', type: 'texte' },
              ]}
              initial={rendezVous.initial}
              libelleBouton="Préparer le rendez-vous"
              onAnnuler={() => setRendezVous({ etape: 'ferme' })}
              onValider={({ jour, heureDebut, heureFin, ...v }) =>
                preparer({
                  ...v,
                  type: 'rendez_vous',
                  debut: instantDeParis(jour as string, heureDebut as string),
                  fin: heureFin ? instantDeParis(jour as string, heureFin as string) : null,
                })
              }
            />
          )}
          {navigation.etape === 'lecture' && (
            <p className="discret">Lecture de la tâche et de son mail…</p>
          )}
          {navigation.etape === 'ouvert' && navigation.note && (
            <p className="discret">{navigation.note}</p>
          )}
          {navigation.etape === 'ouvert' && (
            <Formulaire
              initial={{ objectif: navigation.objectif, site: navigation.site }}
              champs={[
                {
                  nom: 'objectif',
                  libelle: 'Objectif',
                  type: 'texte',
                  aide: 'Ce que l’agent doit faire sur le site. Vide : le titre de la tâche.',
                },
                { nom: 'site', libelle: 'Site de départ (https://…)', type: 'texte' },
              ]}
              libelleBouton="Préparer le prompt"
              onAnnuler={() => setNavigation({ etape: 'ferme' })}
              onValider={(v) => preparer({ ...v, type: 'navigation' })}
            />
          )}
        </>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {envois.data?.map((b) => (
        <div key={b.id}>
          <button
            type="button"
            className="ligne ligne-lien"
            aria-expanded={relu === b.id}
            onClick={() => setRelu(relu === b.id ? null : b.id)}
          >
            <div className="ligne-contenu">
              {b.objet} <span className="badge">{LIBELLES_TYPE_BROUILLON[b.type]}</span>
              <span className="badge">{LIBELLES_STATUT_BROUILLON[b.statut]}</span>
            </div>
            <Icone nom={relu === b.id ? 'keyboard_arrow_down' : 'chevron_right'} />
          </button>
          {relu === b.id && <EnvoiDansLaTache id={b.id} />}
        </div>
      ))}
    </Section>
  )
}
